import { useCallback, useEffect, useRef, useState } from "react";

import ReportService from "src/Services/API/report.service";
import { handleError } from "src/Common/function";

// The Time Management endpoint returns every row for the range in one response.
//
// It used to be the 10-row ad-hoc *preview* endpoint, walked page by page. That
// was expensive in a way the page size hid: each request rebuilt the entire
// dataset server-side before slicing ten rows out, so the whole query ran once
// per ten rows displayed. The server now caps the set and says so, which is why
// there is no page budget here any more.
// Fallback only. The live figure comes back on the response as `maxRows`, so
// the message a user reads cannot drift from the limit the server applied — a
// hard-coded copy here would quietly start lying the day the server's changes.
export const MAX_ROWS = 25000;

const useHoursWorked = (args) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recordsFound, setRecordsFound] = useState(0);
  const [capped, setCapped] = useState(false);
  const [maxRows, setMaxRows] = useState(MAX_ROWS);
  // Whether the server derived otSummary over the whole range or only over the
  // rows it returned. Defaults to false and is only ever set from the response:
  // an older deployment does not send it, and assuming the better behaviour
  // would have this screen vouch for totals that are actually partial.
  const [totalsComplete, setTotalsComplete] = useState(false);
  // Technician-weeks already decided as two, so the buttons show the choice as
  // made rather than as defaulted.
  const [splitWeeks, setSplitWeeks] = useState([]);
  // Derived overtime, one entry per technician. Computed server-side over the
  // whole range and repeated on every page, so the first response is enough.
  const [otSummary, setOtSummary] = useState([]);

  // Every run takes a token. A run whose token is stale — because the filters
  // changed or the component unmounted mid-walk — drops its results instead of
  // writing them over the newer run's. Without this, a slow early request can
  // land after a fast later one and repopulate the table with stale rows.
  const runToken = useRef(0);
  // The args of the current load, so a refresh can repeat it.
  const currentArgs = useRef(null);
  // A refresh after a save: the same request, but the rows on screen stay
  // put until the new ones arrive, and the page keeps its expanded rows,
  // sort and scroll. Overtime attribution can move across a whole week when
  // one day changes, so the figures do have to come back from the server —
  // the screen just does not have to empty while they do.
  const [refreshing, setRefreshing] = useState(false);

  const run = useCallback(async (requestArgs, token, { silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setCapped(false);
      setRecordsFound(0);
      setRows([]);
      setOtSummary([]);
    }

    try {
      const response = await ReportService.getTimeManagementHours(
        0,
        requestArgs.past7Days,
        requestArgs.past30Days,
        requestArgs.customDays,
        requestArgs.customStartDate,
        requestArgs.customEndDate,
        requestArgs.date,
        requestArgs.filters
      );

      if (runToken.current !== token) return;

      const data = response?.data || {};
      const pageRows = Array.isArray(data.rows) ? data.rows : [];
      setRows(pageRows);
      setRecordsFound(Number(data.recordsFound) || pageRows.length);
      setCapped(Boolean(data.capped));
      setMaxRows(Number(data.maxRows) || MAX_ROWS);
      setTotalsComplete(data.totalsComplete === true);
      setSplitWeeks(Array.isArray(data.splitWeeks) ? data.splitWeeks : []);
      setOtSummary(Array.isArray(data.otSummary) ? data.otSummary : []);
    } catch (error) {
      if (runToken.current !== token) return;
      handleError(error);
    } finally {
      if (runToken.current === token) {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    if (!currentArgs.current) return;
    const token = runToken.current + 1;
    runToken.current = token;
    run(currentArgs.current, token, { silent: true });
  }, [run]);

  useEffect(() => {
    const token = runToken.current + 1;
    runToken.current = token;

    currentArgs.current = args;
    if (!args) {
      setRows([]);
      setLoading(false);
      setRecordsFound(0);
      setCapped(false);
      setOtSummary([]);
      return undefined;
    }

    run(args, token);
    // Invalidate this run on unmount or when args change.
    return () => {
      runToken.current += 1;
    };
  }, [args, run]);

  return { rows, loading, refreshing, refresh, recordsFound, capped, maxRows, totalsComplete, splitWeeks, otSummary };
};

export default useHoursWorked;
